import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react'

// import { cn } from '@/v2/utils'
// import { cn } from '@/v2/utils/utils.ts'
import { cn } from '../../utils/utils'
import { CheckmarkIcon } from './icons/checkmark'
import { CaretDownIcon } from './icons/caret-down'

function Select({ ...props }: React.ComponentProps<typeof SelectPrimitive.Root>) {
    return <SelectPrimitive.Root data-slot="select" {...props} />
}

function SelectGroup({ ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) {
    return <SelectPrimitive.Group data-slot="select-group" {...props} />
}

function SelectValue({ ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) {
    return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

function SelectTrigger({ className, children, ...props }: React.ComponentProps<typeof SelectPrimitive.Trigger>) {
    return (
        <SelectPrimitive.Trigger
            data-slot="select-trigger"
            className={cn(
                "group border-input data-[placeholder]:text-grey80 [&_svg:not([class*='text-'])]:text-grey80 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-fit items-center justify-between gap-2 rounded-md border bg-white px-[14px] py-[12px] text-base whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:bg-grey30  h-auto *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 hover:bg-grey10",
                className
            )}
            {...props}
        >
            {children}
            <SelectPrimitive.Icon asChild>
                {/* <ChevronDownIcon className="size-4 opacity-50" /> */}
                <CaretDownIcon className="transition-transform group-data-[state=open]:rotate-180" />
            </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
    )
}

function SelectContent({
    className,
    children,
    position = 'popper',
    align = 'center',
    ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
    return (
        <SelectPrimitive.Portal>
            <SelectPrimitive.Content
                data-slot="select-content"
                className={cn(
                    'bg-white text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-md',
                    position === 'popper' &&
                        'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
                    className
                )}
                position={position}
                align={align}
                {...props}
            >
                <SelectScrollUpButton />
                <SelectPrimitive.Viewport
                    className={cn(
                        'p-0',
                        position === 'popper' &&
                            'divide-y divide-grey40 h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1'
                    )}
                >
                    {children}
                </SelectPrimitive.Viewport>
                <SelectScrollDownButton />
            </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
    )
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
    return (
        <SelectPrimitive.Label data-slot="select-label" className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)} {...props} />
    )
}

function SelectItem({ className, children, ...props }: React.ComponentProps<typeof SelectPrimitive.Item>) {
    return (
        <SelectPrimitive.Item
            data-slot="select-item"
            className={cn(
                "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm px-[14px] py-[12px] text-base outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2 hover:bg-grey10 data-[state=checked]:bg-blue10",
                className
            )}
            {...props}
        >
            <span className="absolute right-2 flex size-3.5 items-center justify-center text-blue40">
                <SelectPrimitive.ItemIndicator>
                    <CheckmarkIcon width={16} height={16} />
                </SelectPrimitive.ItemIndicator>
            </span>
            <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        </SelectPrimitive.Item>
    )
}

function SelectSeparator({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Separator>) {
    return (
        <SelectPrimitive.Separator
            data-slot="select-separator"
            className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
            {...props}
        />
    )
}

function SelectScrollUpButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
    return (
        <SelectPrimitive.ScrollUpButton
            data-slot="select-scroll-up-button"
            className={cn('flex cursor-default items-center justify-center py-1', className)}
            {...props}
        >
            <ChevronUpIcon className="size-4" />
        </SelectPrimitive.ScrollUpButton>
    )
}

function SelectScrollDownButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
    return (
        <SelectPrimitive.ScrollDownButton
            data-slot="select-scroll-down-button"
            className={cn('flex cursor-default items-center justify-center py-1', className)}
            {...props}
        >
            <ChevronDownIcon className="size-4" />
        </SelectPrimitive.ScrollDownButton>
    )
}

type SelectOptions = {
    value: any // Changed from string to any to support numbers, booleans, etc.
    label: string
}[]

type SelectElementProps = {
    name?: string
    value?: any // Changed from string to any to support any value type
    onValueChange?: (value: any) => void // Changed to return original type instead of string
    placeholder?: string
    disabled?: boolean
    options: SelectOptions
    error?: boolean
}

/**
 * SelectElement component that handles value type conversion.
 *
 * Problem: Radix UI Select (and HTML selects in general) only work with string values.
 * This means if you pass a number like 25 as a value, it gets converted to "25" (string),
 * and when the user selects it, onValueChange receives "25" instead of 25.
 *
 * Solution: We maintain a map of stringified values to their original types.
 * When a selection is made, we look up the original value and return that instead.
 *
 * This ensures:
 * 1. Form state maintains correct types (number stays number, boolean stays boolean)
 * 2. Select properly shows the selected value (comparison works: value={25} matches option.value={25})
 * 3. No breaking changes to the API - users can pass any value type
 */
export function SelectElement({ placeholder, options, disabled, name, value, onValueChange, error = false }: SelectElementProps) {
    // Create a map: stringified value -> original value
    // This lets us convert back to the original type when a selection is made
    // Memoized to avoid recreating the map on every render
    const valueMap = React.useMemo(() => {
        const map = new Map()
        options.forEach(opt => {
            // Store mapping of string representation to original value
            // e.g., "25" -> 25, "true" -> true, "EUR" -> "EUR"
            map.set(String(opt.value), opt.value)
        })
        return map
    }, [options])

    // Convert all option values to strings for Radix UI
    // Radix only accepts string values, so we stringify them here
    const stringOptions = React.useMemo(
        () =>
            options.map(opt => ({
                value: String(opt.value), // Convert to string: 25 -> "25"
                label: opt.label,
            })),
        [options]
    )

    // Convert the current value to string for Radix
    // If value is 25, this becomes "25" so Radix can match it with options
    const stringValue = value !== undefined && value !== null ? String(value) : undefined

    return (
        <Select
            disabled={disabled}
            name={name}
            value={stringValue}
            onValueChange={stringifiedValue => {
                // When user selects an option, Radix gives us the stringified value ("25")
                // We look it up in our map to get the original value (25) and pass that back
                const originalValue = valueMap.get(stringifiedValue)
                onValueChange?.(originalValue)
            }}
        >
            <SelectTrigger className={`w-full min-w-0 truncate ${error ? 'border-red20' : ''}`}>
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
                {stringOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                        {option.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    )
}

export {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectScrollDownButton,
    SelectScrollUpButton,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
}
